import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { serializePayment } from "@/lib/schemas/checkout/payment";
import {
  type Banregio3DSResponse,
  Banregio3DSResponseSchema,
} from "@/lib/schemas/paycode/banregio-3ds-response";
import {
  type CardResponse,
  CardResponseSchema,
} from "@/lib/schemas/paycode/card-response";
import { CashDetailsSchema } from "@/lib/schemas/paycode/cash-details";
import { CieDetailsSchema } from "@/lib/schemas/paycode/cie-details";
import type {
  PaycodeResponse,
  PaycodeResponseError,
} from "@/lib/schemas/paycode/paycode-response";
import {
  type SafekeyResponse,
  SafekeyResponseSchema,
} from "@/lib/schemas/paycode/safekey-response";
import { SpeiDetailsSchema } from "@/lib/schemas/paycode/spei-details";
import { useCheckoutStore } from "@/lib/store/checkout";
import { CodiDetailsSchema } from "../schemas/paycode/codi-details";
import { TpvBarcodeDetailsSchema } from "../schemas/paycode/tpv-barcode-details";

export type CardPaymentResponse =
  | CardResponse
  | SafekeyResponse
  | Banregio3DSResponse;

export class PaycodeError extends Error {}

const extractMessage = (error?: PaycodeResponseError | string) => {
  const message = typeof error === "object" ? error?.message : error;
  return message || "Payment request failed";
};

const postPayment = async <T>(method: string, body: unknown): Promise<T> => {
  const response = await fetch(`/api/payment/${method}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) throw new PaycodeError(extractMessage(data?.error));

  return data as T;
};

const postEnveloped = async <T>(method: string, body: unknown): Promise<T> => {
  const pcResponse = await postPayment<PaycodeResponse<T>>(method, body);

  if (!pcResponse.success) {
    throw new PaycodeError(extractMessage(pcResponse.error));
  }

  return pcResponse.data;
};

interface InlineResponse {
  success: boolean;
  error?: PaycodeResponseError | string;
}

const postInline = async (method: string, body: unknown): Promise<unknown> => {
  const data = await postPayment<InlineResponse>(method, body);

  if (!data.success) throw new PaycodeError(extractMessage(data.error));

  return data;
};

export const useSpeiDetails = () => {
  const hash = useCheckoutStore((s) => s.hash);

  return useQuery({
    queryKey: ["payment", "spei", hash],
    queryFn: async () =>
      SpeiDetailsSchema.parse(await postInline("spei", serializePayment())),
    enabled: !!hash,
  });
};

export const useCashDetails = () => {
  const hash = useCheckoutStore((s) => s.hash);

  return useQuery({
    queryKey: ["payment", "cash", hash],
    queryFn: async () =>
      CashDetailsSchema.parse(await postEnveloped("cash", { hash })),
    enabled: !!hash,
  });
};

export const useTpvBarcodeDetails = () => {
  const hash = useCheckoutStore((s) => s.hash);

  return useQuery({
    queryKey: ["payment", "tpvBarcode", hash],
    queryFn: async () =>
      TpvBarcodeDetailsSchema.parse(
        await postEnveloped("tpv-barcode", serializePayment()),
      ),
    enabled: !!hash,
  });
};

export const useCodiDetails = () => {
  const hash = useCheckoutStore((s) => s.hash);

  return useQuery({
    queryKey: ["payment", "codi", hash],
    queryFn: async () => {
      const details = CodiDetailsSchema.parse(
        await postEnveloped("codi", serializePayment()),
      );

      if (details.result === false) {
        if (details.error) toast.error(details.error);
        throw new PaycodeError(details.error || "CoDi details failed to load");
      }

      return details;
    },
    enabled: !!hash,
  });
};

export const useCieDetails = () => {
  const hash = useCheckoutStore((s) => s.hash);

  return useQuery({
    queryKey: ["payment", "cie", hash],
    queryFn: async () =>
      CieDetailsSchema.parse(await postEnveloped("cie", serializePayment())),
    enabled: !!hash,
  });
};

// Discriminate on the raw API fields, then run the matching schema's transform.
const parseCardPayment = (data: unknown): CardPaymentResponse => {
  if (typeof data === "object" && data !== null && "acsURL" in data)
    return SafekeyResponseSchema.parse(data);
  if (typeof data === "object" && data !== null && "url_threeds" in data)
    return Banregio3DSResponseSchema.parse(data);
  return CardResponseSchema.parse(data);
};

export const useCardPayment = () =>
  useMutation({
    mutationFn: async () =>
      parseCardPayment(await postEnveloped("card", serializePayment())),
  });
